import { CustomLoader } from "@admin/components/ui/custom-loader";
import { Card, CardContent } from "../../components/ui/card";
import { DataTable, type TableColumnDef } from "../../components/data-table";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Eye } from "lucide-react";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
  contactFormsAPI,
  type ContactForm,
  PaginationParams,
} from "../../services/api";
import { formatDate } from "../../lib/utils";
import { CommonPagination } from "../../components/CommonPagination";

export default function ContactQueries() {
  const [contacts, setContacts] = useState<ContactForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | number | null>(null);
  const [selectedContact, setSelectedContact] = useState<ContactForm | null>(
    null
  );
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params: PaginationParams = {
        page: currentPage,
        page_size: pageSize,
      };
      const res = await contactFormsAPI.list(params);
      if (res && typeof res === "object") {
        const list = res.results ?? res.data ?? [];
        setContacts(Array.isArray(list) ? list : []);
        const count =
          res.count ??
          (res as any).total_count ??
          (Array.isArray(list) ? list.length : 0);
        setTotalItems(count);
        setTotalPages(Math.ceil(count / pageSize));
      }
    } catch {
      toast.error("Failed to load contact submissions");
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleViewContact = async (contact: ContactForm) => {
    setSelectedId(contact.id);
    setSelectedContact(null);
    setReplyText("");
    setLoadingDetail(true);
    try {
      const detail = await contactFormsAPI.getById(contact.id);
      setSelectedContact(detail ?? contact);
    } catch {
      toast.error("Failed to load contact details");
      setSelectedContact(contact);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleReply = async () => {
    if (
      selectedId === null ||
      !selectedContact?.email?.trim() ||
      !replyText.trim()
    ) {
      return;
    }
    setSendingReply(true);
    try {
      await contactFormsAPI.sendMail(selectedId, {
        email: selectedContact.email.trim(),
        response: replyText.trim(),
      });
      toast.success("Reply sent successfully!");
      handleCloseDialog();
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { message?: string; detail?: string } };
        message?: string;
      };
      const message =
        ax?.response?.data?.message ??
        ax?.response?.data?.detail ??
        ax?.message ??
        "Failed to send reply. Please try again.";
      toast.error(message);
      // Keep modal open on failure so user can fix and retry
    } finally {
      setSendingReply(false);
    }
  };

  const handleCloseDialog = () => {
    setSelectedId(null);
    setSelectedContact(null);
    setReplyText("");
  };

  const tableColumns = useMemo<TableColumnDef<ContactForm>[]>(
    () => [
      {
        id: "sr",
        label: "Sr. No",
        width: 72,
        alwaysVisible: true,
        renderCell: (_row, rowIndex) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
      },
      {
        id: "name",
        label: "Contact Person Name",
        width: 180,
        renderCell: contact => (
          <span className="font-medium">{contact.name}</span>
        ),
      },
      {
        id: "subject",
        label: "Subject",
        width: 140,
        renderCell: contact => (
          <Badge variant="outline">{contact.subject || "—"}</Badge>
        ),
      },
      {
        id: "phone",
        label: "Phone Number",
        width: 130,
        renderCell: contact => contact.phone_number || "—",
      },
      {
        id: "email",
        label: "Email ID",
        width: 180,
        renderCell: contact => contact.email,
      },
      {
        id: "date",
        label: "Date",
        width: 120,
        renderCell: contact => formatDate(contact.created_at),
      },
      {
        id: "status",
        label: "Status",
        width: 120,
        renderCell: contact => (
          <Badge
            variant={
              contact.status === "new"
                ? "success"
                : contact.status === "in_progress"
                  ? "secondary"
                  : "default"
            }
            className={
              contact.status === "responded" ? "bg-primary" : "text-white"
            }
          >
            {(contact.status ?? "new").replace("_", " ")}
          </Badge>
        ),
      },
      {
        id: "actions",
        label: "Action",
        width: 80,
        alwaysVisible: true,
        renderCell: contact => (
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleViewContact(contact)}
          >
            <Eye className="h-4 w-4" />
          </Button>
        ),
      },
    ],
    [currentPage, pageSize]
  );

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Contact Queries</h1>
        <p className="text-muted-foreground">
          Manage all contact form submissions and inquiries
        </p>
      </div>

      {/* Contact Queries Table */}
      <Card>
        <CardContent className="p-3 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <CustomLoader size="md" />
            </div>
          ) : (
            <DataTable
              title={`All Contact Submissions (${totalItems})`}
              tableId="super-admin-contact-queries"
              columns={tableColumns}
              data={contacts}
              getRowKey={contact => contact.id}
              emptyMessage="No contact submissions yet"
              rowClassName={() => "cursor-pointer hover:bg-gray-100"}
            />
          )}

          {totalItems > 0 && (
            <div className="mt-4 border-t pt-4">
              <CommonPagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalCount={totalItems}
                onPageChange={setCurrentPage}
                onPageSizeChange={size => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contact Detail Dialog */}
      <Dialog open={selectedId !== null} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Contact Query Details</DialogTitle>
          </DialogHeader>
          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <CustomLoader size="md" />
            </div>
          ) : (
            selectedContact && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Contact Person</Label>
                    <p className="font-medium text-foreground">
                      {selectedContact.name}
                    </p>
                  </div>
                  <div>
                    <Label>Subject</Label>
                    <Badge variant="outline">
                      {selectedContact.subject || "—"}
                    </Badge>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <p className="text-foreground">
                      {selectedContact.phone_number || "—"}
                    </p>
                  </div>
                  <div>
                    <Label>Email</Label>
                    <p className="text-foreground">{selectedContact.email}</p>
                  </div>
                  <div>
                    <Label>Date</Label>
                    <p className="text-foreground">
                      {formatDate(selectedContact.created_at)}
                    </p>
                  </div>
                  {selectedContact.status && (
                    <div>
                      <Label>Status</Label>
                      <Badge variant="secondary">
                        {selectedContact.status.replace("_", " ")}
                      </Badge>
                    </div>
                  )}
                </div>

                <div>
                  <Label>Message</Label>
                  <div className="mt-2 rounded-lg bg-accent p-4">
                    <p className="whitespace-pre-wrap text-white">
                      {selectedContact.message}
                    </p>
                  </div>
                </div>

                {selectedContact.status === "responded" && (
                  <div>
                    <Label>Previous Response</Label>
                    <div className="mt-2 rounded-lg border border-primary/20 bg-primary/10 p-4">
                      <p className="text-sm text-foreground">
                        {selectedContact.response?.trim() ??
                          "Thank you for reaching out. We have received your inquiry and will get back to you shortly."}
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="response">Response</Label>
                  <Textarea
                    id="response"
                    placeholder="Type your response..."
                    rows={5}
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={handleCloseDialog}
                    disabled={sendingReply}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleReply}
                    disabled={!replyText.trim() || sendingReply}
                  >
                    {sendingReply ? (
                      <>
                        <CustomLoader size="xs" className="mr-2" />
                        Sending...
                      </>
                    ) : (
                      "Send Reply"
                    )}
                  </Button>
                </div>
              </div>
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
